import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";

// Only the first 2 modules are revealed — the rest live behind the $3 paywall.
const NUMS = ["01", "02"];

const Icon = ({ n, color }) => {
  const paths = {
    "01": <path d="M4 20V10l8-6 8 6v10M9 20v-6h6v6" stroke={color} strokeWidth="1.6" fill="none" strokeLinejoin="round"/>,
    "02": <path d="M4 8h16M4 14h10M4 20h16" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>,
  };
  return (
    <svg viewBox="0 0 24 24" width="30" height="30" fill="none">
      {paths[n] || paths["01"]}
    </svg>
  );
};

export default function BentoModules({ onBuy }) {
  const { t } = useI18n();
  // Hidden-module titles for the teaser list (first 4 of the 8 that stay inside)
  const hiddenPreview = t.modules.slice(2, 6);
  const totalHidden = t.modules.length - 2;

  return (
    <section
      id="inside"
      className="relative py-24 md:py-32 bg-[var(--cream)]"
      data-testid="whats-inside-section"
    >
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div className="max-w-2xl">
            <div className="chip-num mb-4"><span>{t.bento.chip}</span></div>
            <h2 className="font-display font-medium text-[2.4rem] sm:text-[3rem] lg:text-[3.8rem] leading-[0.98] tracking-[-0.025em] text-[var(--ink)]">
              {t.bento.h2_a}{" "}
              <span className="font-editorial-italic text-[var(--marigold)]">
                {t.bento.h2_italic}
              </span>{" "}
              {t.bento.h2_b}
            </h2>
          </div>
          <p className="max-w-sm text-[var(--muted)] text-[15px] leading-relaxed">
            {t.bento.note_a}{" "}
            <span className="underline-marigold">{t.bento.note_link}</span>{" "}
            {t.bento.note_b}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5" data-testid="bento-modules-grid">
          {/* Module 01 — ink */}
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
            className="bento-card rounded-[22px] p-7 md:p-9 min-h-[320px] md:min-h-[380px]"
            style={{ background: "#0F1F17", color: "#FDFBF7", border: "1px solid transparent" }}
            data-testid="bento-module-01"
          >
            <span className="bento-num" style={{ color: "#E65C00" }}>01</span>
            <div className="flex flex-col h-full justify-between gap-6">
              <div>
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-5"
                  style={{ background: "rgba(253,251,247,0.14)", color: "#E65C00" }}
                >
                  <Icon n="01" color="#E65C00" />
                </div>
                <div className="font-mono-caption text-[10px] tracking-[0.22em] uppercase mb-3 opacity-70">
                  {t.bento.module_prefix} 01 · {t.modules[0].tag}
                </div>
                <h3 className="font-display font-medium leading-[1.02] tracking-tight text-[2rem] md:text-[2.4rem]">
                  {t.modules[0].title}
                </h3>
              </div>
              <p className="text-[15px] leading-relaxed opacity-85 max-w-[46ch]">
                {t.modules[0].desc}
              </p>
            </div>
          </motion.article>

          {/* Module 02 — marigold */}
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1], delay: 0.08 }}
            className="bento-card rounded-[22px] p-7 md:p-9 min-h-[320px] md:min-h-[380px]"
            style={{ background: "#E65C00", color: "#FDFBF7", border: "1px solid transparent" }}
            data-testid="bento-module-02"
          >
            <span className="bento-num" style={{ color: "#0F1F17" }}>02</span>
            <div className="flex flex-col h-full justify-between gap-6">
              <div>
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-5"
                  style={{ background: "rgba(253,251,247,0.18)", color: "#0F1F17" }}
                >
                  <Icon n="02" color="#0F1F17" />
                </div>
                <div className="font-mono-caption text-[10px] tracking-[0.22em] uppercase mb-3 opacity-70">
                  {t.bento.module_prefix} 02 · {t.modules[1].tag}
                </div>
                <h3 className="font-display font-medium leading-[1.02] tracking-tight text-[2rem] md:text-[2.4rem]">
                  {t.modules[1].title}
                </h3>
              </div>
              <p className="text-[15px] leading-relaxed opacity-90 max-w-[46ch]">
                {t.modules[1].desc}
              </p>
            </div>
          </motion.article>
        </div>

        {/* Teaser — hidden modules */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.2, 0.8, 0.2, 1] }}
          className="mt-4 md:mt-5 rounded-[22px] p-7 md:p-10 relative overflow-hidden"
          style={{ background: "var(--cream-2)", border: "1px dashed rgba(15,31,23,0.2)" }}
          data-testid="bento-teaser"
        >
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-14 items-center">
            <div>
              <div className="font-mono-caption text-[10px] tracking-[0.22em] uppercase text-[var(--marigold)] mb-3">
                {t.bento_teaser.chip}
              </div>
              <h3 className="font-display font-medium text-[1.9rem] md:text-[2.4rem] leading-[1.02] tracking-tight text-[var(--ink)]">
                {t.bento_teaser.h_a}{" "}
                <span className="font-editorial-italic text-[var(--marigold)]">
                  {totalHidden} {t.bento_teaser.h_italic}
                </span>{" "}
                {t.bento_teaser.h_b}
              </h3>
              <p className="mt-4 text-[var(--muted)] text-[15px] leading-relaxed max-w-[46ch]">
                {t.bento_teaser.p}
              </p>
              {onBuy && (
                <button
                  onClick={onBuy}
                  className="btn-pill btn-primary mt-6 text-[14px]"
                  data-testid="bento-teaser-buy"
                >
                  <span>{t.bento_teaser.cta}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              )}
            </div>

            {/* Blurred locked list */}
            <ul className="relative space-y-3">
              {hiddenPreview.map((m, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3 py-3 px-4 rounded-xl bg-[var(--cream)] border border-[rgba(15,31,23,0.06)]"
                  style={{
                    filter: `blur(${i === 0 ? "0.5px" : i === 1 ? "1.5px" : i === 2 ? "3px" : "5px"})`,
                    opacity: 1 - i * 0.18,
                  }}
                >
                  <span className="font-mono-caption text-[10px] text-[var(--marigold)] tracking-[0.2em]">
                    {String(i + 3).padStart(2, "0")}
                  </span>
                  <span className="font-display text-[1.05rem] text-[var(--ink)] leading-none">
                    {m.title}
                  </span>
                  <svg className="ml-auto text-[var(--muted)]" width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <rect x="4" y="11" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="1.6"/>
                    <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.6"/>
                  </svg>
                </li>
              ))}
              <div className="absolute inset-x-0 bottom-0 h-24 pointer-events-none" style={{
                background: "linear-gradient(to bottom, transparent 0%, var(--cream-2) 95%)",
              }} />
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
